import React, { useState, useEffect, useMemo } from 'react';
import { UserPlus, User, Search, Eye, FileText, Mail, Fingerprint, GraduationCap, ChevronRight, Maximize2, Minimize2, ArrowLeft, X, CheckCircle, Clock, AlertCircle, MessageSquare, Trash2, RotateCcw } from 'lucide-react';
import AuthorityEnrollmentModal from './AuthorityEnrollmentModal';
import api from '../api';

const CAUserManagement = ({ onClose, userRole }) => {
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [showRejected, setShowRejected] = useState(false);
    
    // States for "Observation"
    const [showObservationModal, setShowObservationModal] = useState(false);
    const [targetDoc, setTargetDoc] = useState(null);
    const [observationText, setObservationText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);

    // Only ACO can perform operations (Approve, Reject, Enroll, Update Doc Status)
    const canOperate = userRole === 'ACO';

    const roleLabels = {
        'AL': 'Alumno',
        'ACO': 'Auxiliar de la Coord. Operativa',
        'CD': 'Coord. de Diplomatura',
        'CG': 'Coordinadora General',
        'CO': 'Coordinador Operativo',
        'PR': 'Profesor',
        'DO': 'Docente',
        'RM': 'Ref. Municipal',
        'CA': 'Coordinadora Académica'
    };

    useEffect(() => {
        fetchUsers();
    }, [showRejected]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get(`users/?rejected=${showRejected}`);
            setUsers(res.data);
            
            // Sync selectedUser if already selected
            if (selectedUser) {
                const refreshedUser = res.data.find(u => u.id === selectedUser.id);
                if (refreshedUser) setSelectedUser(refreshedUser);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        if (!canOperate) return;
        try {
            await api.post(`users/${id}/approve/`);
            fetchUsers();
        } catch (err) {
            console.error("Error approving user:", err);
            alert("Error al aprobar usuario.");
        }
    };

    const handleUnapprove = async (id) => {
        if (!canOperate) return;
        if (window.confirm('¿Desea quitar el visto bueno y dejar al usuario como pendiente?')) {
            try {
                await api.post(`users/${id}/unapprove/`);
                fetchUsers();
            } catch (err) {
                console.error("Error unapproving user:", err);
            }
        }
    };

    const handleReject = async (id) => {
        if (!canOperate) return;
        const msg = showRejected ? '¿Está seguro de que desea restaurar este usuario?' : '¿Está seguro de que desea restringir y mandar a la papelera a este usuario?';
        if (window.confirm(msg)) {
            try {
                if (showRejected) {
                    await api.post(`users/${id}/approve/`);
                } else {
                    await api.post(`users/${id}/reject/`);
                }
                if (selectedUser?.id === id) setSelectedUser(null);
                fetchUsers();
            } catch (err) {
                console.error("Error rejecting user:", err);
            }
        }
    };

    const handleResetPassword = async (user) => {
        if (window.confirm(`¿Desea restablecer la contraseña de ${user.email} a la predeterminada?`)) {
            try {
                const res = await api.post(`users/${user.id}/reset_any_password/`);
                alert(`${res.data.status}\nNueva contraseña: ${res.data.temp_password}\nEstado correo: ${res.data.email_status}`);
                fetchUsers();
            } catch (err) {
                console.error(err);
                alert('Error al restablecer la contraseña. Verifique los permisos.');
            }
        }
    };

    const handleUpdateStatus = async (docId, newStatus, observation = '') => {
        if (!canOperate) return;
        try {
            await api.patch(`student-documents/${docId}/`, {
                status: newStatus,
                observation: observation
            });
            
            // Refresh local state
            if (selectedUser) {
                const updatedDocs = selectedUser.profile.documents.map(d => 
                    d.id === docId ? { ...d, status: newStatus, status_display: getStatusLabel(newStatus), observation } : d
                );
                setSelectedUser({
                    ...selectedUser,
                    profile: {
                        ...selectedUser.profile,
                        documents: updatedDocs
                    }
                });
            }
            
            // If it was an observation, send a message
            if (newStatus === 'OB') {
                await api.post('messages/', {
                    recipient_type: 'individual',
                    recipient_id: selectedUser.email,
                    subject: `Observación en documento: ${targetDoc.doc_type_display}`,
                    text: `Se ha realizado una observación en tu documento "${targetDoc.doc_type_display}": ${observation}`,
                    date: new Date().toISOString().split('T')[0]
                });
                setShowObservationModal(false);
                setObservationText('');
                setTargetDoc(null);
                alert("Observación guardada y notificación enviada al usuario.");
            }

            fetchUsers(); // Update the main list too
        } catch (err) {
            console.error('Error updating document status:', err);
            alert("No se pudo actualizar el estado del documento.");
        }
    };

    const getStatusLabel = (status) => {
        const labels = { 'PE': 'Pendiente', 'VI': 'Visto', 'OB': 'Observado' };
        return labels[status] || status;
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'VI': return <CheckCircle size={16} className="text-success" />;
            case 'OB': return <AlertCircle size={16} className="text-danger" />;
            default: return <Clock size={16} className="text-warning" />;
        }
    };

    const filteredUsers = useMemo(() => {
        return [...users]
            .filter(u => {
                const searchLower = searchTerm.toLowerCase();
                const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
                return u.email.toLowerCase().includes(searchLower) || fullName.includes(searchLower);
            })
            .sort((a, b) => {
                const nameA = (a.first_name || a.email).toLowerCase();
                const nameB = (b.first_name || b.email).toLowerCase();
                return nameA.localeCompare(nameB);
            });
    }, [users, searchTerm]);

    const modalStyles = {
        width: isMaximized ? '100vw' : '98%',
        maxWidth: isMaximized ? '100vw' : '1400px',
        height: isMaximized ? '100dvh' : '90vh',
        maxHeight: isMaximized ? 'none' : '90vh',
        borderRadius: isMaximized ? '0' : '20px',
        margin: isMaximized ? '0' : 'auto',
        position: isMaximized ? 'fixed' : 'relative',
        top: isMaximized ? '0' : 'auto',
        left: isMaximized ? '0' : 'auto',
        zIndex: isMaximized ? 2000 : 1,
    };

    return (
        <div className="modal-overlay" onClick={onClose} style={{ padding: isMaximized ? '0' : '1rem', zIndex: 1050 }}>
            <div className="modal-content glassmorphism animate-fade-in d-flex flex-column" 
                 style={modalStyles}
                 onClick={e => e.stopPropagation()}>
                
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom border-dark border-opacity-10 pb-3">
                    <div className="d-flex align-items-center gap-3">
                        {selectedUser && (
                            <button 
                                onClick={() => setSelectedUser(null)} 
                                className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                            >
                                <ArrowLeft size={18} /> <span className="d-none d-md-inline">Volver</span>
                            </button>
                        )}
                        <h3 className="mb-0 d-flex align-items-center section-title text-dark" style={{ fontSize: '1.25rem', fontWeight: '800' }}>
                            <UserPlus className="me-2 text-primary" size={24} /> 
                            {!selectedUser ? 'Gestión Unificada de Usuarios' : `Ficha de Usuario: ${selectedUser.first_name}`}
                        </h3>
                    </div>
                    
                    <div className="d-flex align-items-center gap-2">
                        <button onClick={() => setIsMaximized(!isMaximized)} className="btn btn-action info" title={isMaximized ? "Restaurar" : "Maximizar"}>
                            {isMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                        </button>
                        <button type="button" className="btn-close-premium shadow-sm" onClick={onClose} style={{ backgroundColor: '#f43f5e', color: 'white', border: 'none' }}>
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="row g-4 flex-grow-1 overflow-hidden">
                    <div className={`${selectedUser && !isMaximized ? 'col-md-5 d-none d-md-flex' : selectedUser ? 'd-none' : 'col-12'} d-flex flex-column h-100`}>
                        <div className="d-flex gap-2 mb-4">
                            <div className="position-relative flex-grow-1">
                                <Search className="position-absolute top-50 translate-middle-y ms-3 text-warning opacity-75" size={20} />
                                <input 
                                    type="text" 
                                    className="form-control ps-5 premium-search-input" 
                                    placeholder="Buscar por nombre o email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button 
                                onClick={() => setShowRejected(!showRejected)} 
                                className={`btn d-flex align-items-center gap-2 px-3 border-2 ${showRejected ? 'btn-danger bg-opacity-75' : 'btn-outline-secondary'}`}
                                title={showRejected ? "Volver a usuarios activos" : "Ver papelera de restringidos"}
                            >
                                <Trash2 size={18} />
                                <span className="d-none d-md-inline">{showRejected ? 'Restringidos' : 'Papelera'}</span>
                            </button>
                        </div>

                        <div className="table-responsive flex-grow-1 border rounded border-secondary border-opacity-25 bg-dark bg-opacity-25">
                            <table className="table premium-table mb-0">
                                <thead className="sticky-top bg-dark">
                                    <tr>
                                        <th>Usuario</th>
                                        <th>Rol</th>
                                        <th className="text-center">Estado</th>
                                        <th className="text-end">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="4" className="text-center p-5">Cargando usuarios...</td></tr>
                                    ) : filteredUsers.length > 0 ? (
                                        filteredUsers.map(u => {
                                            return (
                                                <tr key={u.id} className={selectedUser?.id === u.id ? 'table-active' : ''}>
                                                    <td>
                                                        <div className="d-flex flex-column text-start">
                                                            <span className="fw-bold">{u.first_name ? `${u.first_name} ${u.last_name || ''}` : 'Sin Nombre'}</span>
                                                            <small className="text-muted">{u.email}</small>
                                                        </div>
                                                    </td>
                                                     <td>
                                                         <span className="fw-bold text-dark" style={{ fontSize: '0.8rem' }}>
                                                             {roleLabels[u.role] || u.role}
                                                         </span>
                                                     </td>
                                                    <td className="text-center">
                                                        {u.is_rejected ? 
                                                            <span className="badge bg-danger">Restringido</span> :
                                                            u.is_approved ? 
                                                                <span className="badge bg-success">Aprobado</span> : 
                                                                <span className="badge bg-warning text-dark">Pendiente</span>
                                                        }
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="d-flex justify-content-end gap-1">
                                                            <button onClick={() => setSelectedUser(u)} className="btn btn-sm btn-action info" title="Ver Detalles"><Eye size={16} /></button>
                                                            {canOperate && (
                                                                <>
                                                                    {!u.is_rejected && !u.is_approved && (
                                                                        <button onClick={() => handleApprove(u.id)} className="btn btn-sm btn-action approve" title="Aprobar"><CheckCircle size={16} /></button>
                                                                    )}
                                                                    <button onClick={() => handleReject(u.id)} className={`btn btn-sm btn-action ${showRejected ? 'approve' : 'reject'}`} title={showRejected ? 'Restaurar' : 'Restringir'}>
                                                                        {showRejected ? <CheckCircle size={16} /> : <Trash2 size={16} />}
                                                                    </button>
                                                                </>
                                                            )}
                                                            <button 
                                                                onClick={() => handleResetPassword(u)} 
                                                                className="btn btn-sm btn-action info" 
                                                                title="Restablecer Contraseña"
                                                            >
                                                                <Fingerprint size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr><td colSpan="4" className="text-center p-5 text-muted fst-italic">No se encontraron usuarios</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {selectedUser && (
                        <div className={`${isMaximized ? 'col-12' : 'col-md-7'} h-100 overflow-auto animate-slide-in-right`}>
                            <div className="card glassmorphism border-warning border-opacity-25 h-100 overflow-auto">
                                <div className="card-body p-3 p-md-4">
                                    <div className="row g-4 mb-4 text-start">
                                        <div className="col-12 d-flex align-items-center gap-3">
                                            <div className="bg-warning bg-opacity-20 p-3 rounded-circle text-warning">
                                                <Fingerprint size={32} />
                                            </div>
                                            <div className="flex-grow-1 text-start">
                                                <h4 className="mb-0 fw-bold">{selectedUser.first_name} {selectedUser.last_name}</h4>
                                                <div className="text-muted d-flex align-items-center gap-2 small">
                                                    <Mail size={14} /> {selectedUser.email} | <GraduationCap size={14} /> {selectedUser.role === 'AL' ? 'Alumno' : 'Personal'}
                                                </div>
                                            </div>
                                            {(canOperate || userRole === 'CD') && (
                                                <div className="ms-auto d-flex gap-2">
                                                    <button 
                                                        className="btn btn-primary-gradient d-flex align-items-center gap-2 py-2 px-3 fw-bold"
                                                        onClick={() => setShowEnrollmentModal(true)}
                                                    >
                                                        <UserPlus size={18} /> Inscribir en Diplo
                                                    </button>
                                                    {canOperate && selectedUser.is_approved && (
                                                        <button 
                                                            className="btn btn-outline-warning d-flex align-items-center gap-2 py-2 px-3 fw-bold"
                                                            onClick={() => handleUnapprove(selectedUser.id)}
                                                        >
                                                            <RotateCcw size={18} /> Quitar Aprobación
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Información Detallada del Usuario */}
                                    <div className="row g-3 mb-4 text-start">
                                        <div className="col-md-4">
                                            <div className="p-3 rounded bg-dark bg-opacity-25 border border-secondary border-opacity-10 h-100">
                                                <div className="small fw-bold text-muted mb-1 d-flex align-items-center gap-1">
                                                    <Fingerprint size={14} /> DNI
                                                </div>
                                                <div className="fs-6 text-dark">{selectedUser.profile?.dni || 'No registrado'}</div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="p-3 rounded bg-dark bg-opacity-25 border border-secondary border-opacity-10 h-100">
                                                <div className="small fw-bold text-muted mb-1 d-flex align-items-center gap-1">
                                                    <Clock size={14} /> EDAD
                                                </div>
                                                <div className="fs-6 text-dark">{selectedUser.profile?.age ? `${selectedUser.profile.age} años` : 'No registrada'}</div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="p-3 rounded bg-dark bg-opacity-25 border border-secondary border-opacity-10 h-100">
                                                <div className="small fw-bold text-muted mb-1 d-flex align-items-center gap-1">
                                                    <CheckCircle size={14} /> ESTADO
                                                </div>
                                                <div className="fs-6 text-dark">{selectedUser.is_rejected ? 'RESTRINGIDO' : selectedUser.is_approved ? 'APROBADO' : 'PENDIENTE'}</div>
                                            </div>
                                        </div>
                                        <div className="col-md-12">
                                            <div className="p-3 rounded bg-dark bg-opacity-25 border border-secondary border-opacity-10 h-100">
                                                <div className="small fw-bold text-muted mb-1 d-flex align-items-center gap-1">
                                                    <GraduationCap size={14} /> ESTUDIOS / FORMACIÓN
                                                </div>
                                                <div className="fs-6 text-dark text-uppercase">{selectedUser.profile?.highest_studies || 'No registrados'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <h5 className="fw-bold mb-3 d-flex align-items-center text-start">
                                        <FileText className="me-2 text-warning" size={20} /> Revisión de Documentación
                                    </h5>
                                    
                                    <div className="d-flex flex-column gap-3">
                                        {selectedUser.profile?.documents && selectedUser.profile.documents.length > 0 ? (
                                            selectedUser.profile.documents.map(doc => (
                                                <div key={doc.id} className="p-3 rounded bg-dark bg-opacity-50 border border-secondary border-opacity-25">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="bg-info bg-opacity-10 p-2 rounded text-info">
                                                                <FileText size={20} />
                                                            </div>
                                                            <div className="text-start">
                                                                <div className="fw-bold">{doc.doc_type_display}</div>
                                                                <div className="d-flex align-items-center gap-1 small text-muted">
                                                                    {getStatusIcon(doc.status)} {getStatusLabel(doc.status)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <a href={doc.file} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-info">
                                                            <Eye size={16} className="me-1" /> Ver Archivo
                                                        </a>
                                                    </div>

                                                    {doc.observation && (
                                                        <div className="alert alert-danger py-2 px-3 small mb-3 text-start">
                                                            <strong>Observación:</strong> {doc.observation}
                                                        </div>
                                                    )}
                                                    {canOperate && (
                                                        <div className="d-flex gap-2 flex-wrap">
                                                            <button 
                                                                onClick={() => handleUpdateStatus(doc.id, 'PE')}
                                                                className={`btn btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1 ${doc.status === 'PE' ? 'btn-warning' : 'btn-outline-warning'}`}
                                                            >
                                                                <Clock size={14} /> Pendiente
                                                            </button>
                                                            <button 
                                                                onClick={() => handleUpdateStatus(doc.id, 'VI')}
                                                                className={`btn btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1 ${doc.status === 'VI' ? 'btn-success' : 'btn-outline-success'}`}
                                                            >
                                                                <CheckCircle size={14} /> Visto / OK
                                                            </button>
                                                            <button 
                                                                onClick={() => { setTargetDoc(doc); setShowObservationModal(true); }}
                                                                className={`btn btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1 ${doc.status === 'OB' ? 'btn-danger' : 'btn-outline-danger'}`}
                                                            >
                                                                <AlertCircle size={14} /> Observar
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center p-5 border border-dashed rounded text-muted">
                                                Este usuario no ha cargado documentación de perfil.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Observation Modal */}
            {showObservationModal && (
                <div className="modal-overlay" style={{ zIndex: 1100, backgroundColor: 'rgba(0,0,0,0.8)' }}>
                    <div className="modal-content glassmorphism animate-scale-in p-4" style={{ maxWidth: '500px', width: '90%' }} onClick={e => e.stopPropagation()}>
                        <h4 className="fw-bold mb-3 d-flex align-items-center text-danger">
                            <AlertCircle className="me-2" /> Observar Documento
                        </h4>
                        <textarea 
                            className="form-control mb-4 bg-dark text-white border-secondary"
                            rows="4"
                            placeholder="Ej: El DNI está borroso..."
                            value={observationText}
                            onChange={(e) => setObservationText(e.target.value)}
                        />
                        <div className="d-flex justify-content-end gap-2">
                            <button className="btn btn-secondary" onClick={() => setShowObservationModal(false)}>Cancelar</button>
                            <button 
                                className="btn btn-danger d-flex align-items-center gap-2"
                                onClick={() => handleUpdateStatus(targetDoc.id, 'OB', observationText)}
                                disabled={!observationText.trim()}
                            >
                                <MessageSquare size={18} /> Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Authority Enrollment Modal */}
            <AuthorityEnrollmentModal 
                isOpen={showEnrollmentModal}
                onClose={() => setShowEnrollmentModal(false)}
                initialEmail={selectedUser?.email}
                onEnrollSuccess={fetchUsers}
            />
        </div>
    );
};

export default CAUserManagement;
